import { Injectable, inject } from '@angular/core';
import {
  Asset,
  DataleanBaseApiService,
  FeatureValue,
  FeatureValueType,
  FilterSort,
  PaginationInfo,
  Parts,
  SearchInfo,
  SortInfo,
  Tag,
  ThumbnailConfig,
} from 'addiction-components';
import { Observable, forkJoin, map, of } from 'rxjs';
import { LocalizationService } from '../../core/locale/localization.service';
import { environment } from '../../../environments/environment';

/** Filtri applicabili alla ricerca degli asset della media library. */
export interface DocumentFilters {
  assetType?: string[];
  creationFrom?: string;
  creationTo?: string;
  updateFrom?: string;
  updateTo?: string;
  tags?: Tag[];
  format?: string[];
}

/** Valori disponibili per costruire il pannello dei filtri, comprensivi dei relativi conteggi. */
export interface DocumentFilterData {
  format: Record<string, number>;
  assetType: Record<string, number>;
  tags?: Tag[];
}

/** Proiezione dei permessi operativi necessari alla visualizzazione degli asset. */
export interface DocumentOperationPermission {
  entityUUID?: string;
  operationList: string[];
}

/** Risposta dell'endpoint dei filtri della media library. */
interface DocumentFiltersResponse {
  features: FeatureValue[];
  filterData: DocumentFilterData;
}

@Injectable({ providedIn: 'root' })
export class DocumentService {
  private readonly baseApi = inject(DataleanBaseApiService);
  private readonly localizationService = inject(LocalizationService);

  /** Recupera l'albero delle categorie asset visibili all'utente corrente. */
  getCategories(): Observable<FeatureValue[]> {
    const locale = this.localizationService.ActiveLanguage || environment.defaultLocale || 'it-IT';

    const additionalParams = {
      parentUUID: '',
      type: FeatureValueType.ASSET,
      locale,
    };
    const featureValues = this.baseApi.getEntities<FeatureValue[]>(environment.featureValueUrl, additionalParams, [
      Parts.FEATURE_VALUES,
      Parts.BASIC_ASSET_INFO,
    ]);

    return featureValues;
  }

  /**
   * Recupera i valori disponibili per i filtri limitandoli alle categorie indicate.
   *
   * @param categoryUuids Categorie che delimitano gli asset interrogabili.
   * @param filters Filtri attualmente applicati.
   * @param search Testo cercato nel nome degli asset.
   */
  getFilters(categoryUuids: string[], filters: DocumentFilters = {}, search?: string): Observable<DocumentFilterData> {
    const additionalParams = {
      ...this.buildFilterParams(categoryUuids, filters),
      locale: environment.defaultLocale,
    };
    const searchInfo = search ? new SearchInfo('name', search, 'searchFields', 'q') : undefined;

    return this.baseApi
      .getEntities<DocumentFiltersResponse>(`${environment.mediaLibraryUrl}filters`, additionalParams, [Parts.FEATURE_VALUES], searchInfo)
      .pipe(map(({ filterData }) => filterData));
  }

  getDocuments(categoryUuids: string[], filters: DocumentFilters, pages: number[], sort: FilterSort, search?: string) {
    const requests = pages.map((page) => {
      const additionalParams = {
        parentUUID: 'all',
        ...this.buildFilterParams(categoryUuids, filters),
      };

      const [sortBy, sortDirection] = sort.value.split('#');
      const searchInfo = search ? new SearchInfo('name', search, 'searchFields', 'q') : undefined;
      return this.baseApi.getEntitiesWithPaginationData<Asset>(
        environment.mediaLibraryUrl,
        additionalParams,
        [Parts.FULL_ASSET_INFO, Parts.TAGS],
        searchInfo,
        new PaginationInfo(environment.pageSize, page),
        new SortInfo(sortBy, sortDirection)
      );
    });

    return forkJoin(requests).pipe(
      map((responses) => {
        const assets: Asset[][] = [];
        responses.forEach((response, index) => {
          assets[pages[index]] = response.result ?? [];
        });
        return {
          assets,
          pages: pages,
          totalItems: responses[0]?.paginationInfo?.totalNumberOfElements ?? 0,
        };
      })
    );
  }

  /** Recupera le configurazioni necessarie a descrivere le thumbnail degli asset. */
  getThumbnailConfigs(): Observable<ThumbnailConfig[]> {
    return this.baseApi.getEntities<ThumbnailConfig[]>(environment.thumbnailConfigUrl, {}, [Parts.EMPTY]);
  }

  /**
   * Recupera i permessi operativi associati agli asset indicati.
   * @param assetUuids
   * @returns
   */
  getOperationPermissions(assetUuids: string[]): Observable<DocumentOperationPermission[]> {
    if (!assetUuids.length) {
      return of([]);
    }
    const additionalParams = { assetsUUIDs: assetUuids, assetsUUIDsMode: 'inclusive' };
    return this.baseApi.createEntity<DocumentOperationPermission[]>(
      `${environment.mediaLibraryUrl}operationPermission`,
      additionalParams,
      []
    );
  }

  downloadAsset(assetUuid: string): Observable<Blob> {
    return this.baseApi.downloadAsset(assetUuid);
  }

  /**
   * Converte i filtri della UI nei parametri attesi dagli endpoint della media library.
   * @param categoryUuids
   * @param filters
   * @returns
   */
  private buildFilterParams(categoryUuids: string[], filters: DocumentFilters = {}): Record<string, string> {
    const queryParams: Record<string, string> = {};

    if (categoryUuids.length) {
      queryParams['featureValueList'] = categoryUuids.join(',');
    }

    if (filters.assetType?.length) {
      queryParams['assetType'] = filters.assetType.join(',');
    }

    if (filters.format?.length) {
      queryParams['format'] = filters.format.join(',');
    }

    if (filters.tags?.length) {
      const tagUuids = filters.tags.map(({ uuid }) => uuid);
      queryParams['tags'] = tagUuids.join(',');
    }

    const creationDateRange = this.serializeDateRange(filters.creationFrom, filters.creationTo);

    if (creationDateRange) {
      queryParams['createDate'] = creationDateRange;
    }

    const updateDateRange = this.serializeDateRange(filters.updateFrom, filters.updateTo);

    if (updateDateRange) {
      queryParams['updateDate'] = updateDateRange;
    }

    return queryParams;
  }

  /**
   * Serializza un intervallo di date secondo la sintassi richiesta dal backend.
   *
   * @param fromDate Estremo iniziale dell’intervallo in formato ISO.
   * @param toDate Estremo finale dell’intervallo in formato ISO.
   * @returns Intervallo serializzato oppure `undefined` quando entrambe le date sono assenti.
   */
  private serializeDateRange(fromDate?: string, toDate?: string): string | undefined {
    if (fromDate && toDate) {
      return `r|${fromDate}|${toDate}`;
    }

    if (fromDate) {
      return `r|${fromDate}`;
    }

    if (toDate) {
      return `r||${toDate}`;
    }

    return undefined;
  }
}
